/**
 * useKrakenChat — Hook for Kraken AI tutor interactions
 *
 * Unlike useInfiniChat (WebSocket), this uses HTTP API calls
 * to POST /api/kraken/chat on mkey.madboat.ai
 */

import { useCallback } from 'react';
import { create } from 'zustand';
import { useAuthStore } from '@/stores/authStore';

const MKEY_API_URL = import.meta.env.VITE_MKEY_API_URL || 'https://mkey-api-production.fidelismadboat.workers.dev';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface KrakenMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  rating?: number;
  krakenState?: 'shadow' | 'adversary' | 'ally' | 'companion';
  isLoading?: boolean;
}

interface KrakenChatState {
  messages: KrakenMessage[];
  sessionId: string | null;
  isTyping: boolean;
  remainingMessages: number;
  tier: string;
  krakenState: string;
  error: string | null;
}

interface KrakenChatActions {
  addMessage: (msg: KrakenMessage) => void;
  updateMessage: (id: string, patch: Partial<KrakenMessage>) => void;
  removeMessage: (id: string) => void;
  setTyping: (v: boolean) => void;
  setError: (e: string | null) => void;
  setMessages: (msgs: KrakenMessage[]) => void;
  setSessionId: (id: string) => void;
  setRemaining: (n: number) => void;
  setTier: (t: string) => void;
  setKrakenState: (s: string) => void;
  reset: () => void;
}

// ---------------------------------------------------------------------------
// Zustand store (internal)
// ---------------------------------------------------------------------------

const initialState: KrakenChatState = {
  messages: [],
  sessionId: null,
  isTyping: false,
  remainingMessages: 10,
  tier: 'free',
  krakenState: 'shadow',
  error: null,
};

export const useKrakenStore = create<KrakenChatState & KrakenChatActions>((set) => ({
  ...initialState,

  addMessage: (msg) =>
    set((s) => ({ messages: [...s.messages, msg] })),

  updateMessage: (id, patch) =>
    set((s) => ({
      messages: s.messages.map((m) => (m.id === id ? { ...m, ...patch } : m)),
    })),

  removeMessage: (id) =>
    set((s) => ({ messages: s.messages.filter((m) => m.id !== id) })),

  setTyping: (isTyping) => set({ isTyping }),
  setError: (error) => set({ error }),
  setMessages: (messages) => set({ messages }),
  setSessionId: (sessionId) => set({ sessionId }),
  setRemaining: (remainingMessages) => set({ remainingMessages }),
  setTier: (tier) => set({ tier }),
  setKrakenState: (krakenState) => set({ krakenState }),

  reset: () => set(initialState),
}));

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function generateId(): string {
  return `kraken_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

async function krakenFetch<T>(
  endpoint: string,
  token: string,
  options: RequestInit = {},
): Promise<T> {
  const url = `${MKEY_API_URL}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: 'Unknown error' }));
    const code = body.code || body.error || `HTTP ${response.status}`;
    throw new KrakenAPIError(response.status, code, body.message || body.error || 'Request failed');
  }

  return response.json();
}

export class KrakenAPIError extends Error {
  constructor(
    public statusCode: number,
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = 'KrakenAPIError';
  }
}

// ---------------------------------------------------------------------------
// Response types from backend
// ---------------------------------------------------------------------------

// Backend response format (maps to our internal types)
interface KrakenChatBackendResponse {
  response: string;
  session_id: string;
  kraken_state?: 'shadow' | 'adversary' | 'ally' | 'companion';
  remaining_messages_today?: number;
  conversation_id: string;
  guardrail?: boolean;
}

interface KrakenChatResponse {
  id: string;
  content: string;
  krakenState?: 'shadow' | 'adversary' | 'ally' | 'companion';
  remainingMessages?: number;
  sessionId?: string;
}

function mapChatResponse(raw: KrakenChatBackendResponse): KrakenChatResponse {
  return {
    id: raw.conversation_id,
    content: raw.response,
    krakenState: raw.kraken_state,
    remainingMessages: raw.remaining_messages_today,
    sessionId: raw.session_id,
  };
}

interface KrakenHistoryBackendResponse {
  messages: Array<{
    id: string;
    role: string;
    content: string;
    created_at: string;
    metadata?: string;
  }>;
  total: number;
}

interface KrakenHistoryResponse {
  messages: KrakenMessage[];
  sessionId?: string;
}

function mapHistoryResponse(raw: KrakenHistoryBackendResponse): KrakenHistoryResponse {
  return {
    messages: (raw.messages || []).map(m => ({
      id: m.id,
      role: m.role as 'user' | 'assistant',
      content: m.content,
      timestamp: m.created_at,
    })).reverse(), // API returns DESC, we need ASC
  };
}

interface KrakenStatusBackendResponse {
  tier: string;
  isPioneer: boolean;
  usedToday: number;
  limit: number;
  remaining: number;
  totalMessages: number;
}

interface KrakenStatusResponse {
  remainingMessages: number;
  tier: string;
  krakenState: string;
  sessionId?: string;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useKrakenChat() {
  const store = useKrakenStore();
  const token = useAuthStore((s) => s.token);

  // ---------- sendMessage ----------
  const sendMessage = useCallback(
    async (content: string, channel?: string, context?: Record<string, unknown>) => {
      if (!token) {
        store.setError('Autenticacao necessaria. Faca login novamente.');
        return;
      }

      if (!content.trim()) return;

      store.setError(null);

      // Optimistic: add user message immediately
      const userMsg: KrakenMessage = {
        id: generateId(),
        role: 'user',
        content: content.trim(),
        timestamp: new Date().toISOString(),
      };
      store.addMessage(userMsg);

      // Show typing indicator
      const loadingId = generateId();
      const loadingMsg: KrakenMessage = {
        id: loadingId,
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
        isLoading: true,
      };
      store.addMessage(loadingMsg);
      store.setTyping(true);

      try {
        const payload: Record<string, unknown> = {
          message: content.trim(),
          ...(channel && { channel }),
          ...(context && { context }),
          ...(store.sessionId && { sessionId: store.sessionId }),
        };

        const raw = await krakenFetch<KrakenChatBackendResponse>(
          '/api/kraken/chat',
          token,
          { method: 'POST', body: JSON.stringify(payload) },
        );
        const res = mapChatResponse(raw);

        // Replace loading message with actual response
        store.updateMessage(loadingId, {
          id: res.id || loadingId,
          content: res.content,
          isLoading: false,
          krakenState: res.krakenState,
          timestamp: new Date().toISOString(),
        });

        // Update metadata from response
        if (res.sessionId) store.setSessionId(res.sessionId);
        if (res.krakenState) store.setKrakenState(res.krakenState);
        if (res.remainingMessages !== undefined) store.setRemaining(res.remainingMessages);
      } catch (err) {
        // Remove loading message on error
        store.removeMessage(loadingId);

        if (err instanceof KrakenAPIError) {
          switch (err.code) {
            case 'BYOK_REQUIRED':
              store.setError('Limite atingido. Traga sua propria chave API ou faca upgrade.');
              break;
            case 'RATE_LIMIT_EXCEEDED':
              store.setError('Muitas mensagens. Aguarde um momento antes de tentar novamente.');
              break;
            case 'SESSION_EXPIRED':
              store.setError('Sessao expirada. Faca login novamente.');
              break;
            default:
              store.setError(err.message);
          }
        } else {
          console.error('[KrakenChat] Unexpected error:', err);
          store.setError('Erro ao comunicar com o Kraken. Tente novamente.');
        }
      } finally {
        store.setTyping(false);
      }
    },
    [token, store],
  );

  // ---------- loadHistory ----------
  const loadHistory = useCallback(
    async (channel?: string) => {
      if (!token) return;

      store.setError(null);

      try {
        const params = new URLSearchParams();
        if (channel) params.set('channel', channel);
        if (store.sessionId) params.set('sessionId', store.sessionId);

        const qs = params.toString();
        const endpoint = `/api/kraken/history${qs ? `?${qs}` : ''}`;

        const raw = await krakenFetch<KrakenHistoryBackendResponse>(endpoint, token);
        const res = mapHistoryResponse(raw);

        store.setMessages(res.messages || []);
        if (res.sessionId) store.setSessionId(res.sessionId);
      } catch (err) {
        console.error('[KrakenChat] Failed to load history:', err);
        store.setError('Erro ao carregar historico de mensagens.');
      }
    },
    [token, store],
  );

  // ---------- rateMessage ----------
  const rateMessage = useCallback(
    async (conversationId: string, rating: number) => {
      if (!token) return;

      try {
        await krakenFetch<{ success: boolean }>(
          '/api/kraken/rate',
          token,
          {
            method: 'POST',
            body: JSON.stringify({ conversationId, rating }),
          },
        );

        // Update the rating on the local message
        store.updateMessage(conversationId, { rating });
      } catch (err) {
        console.error('[KrakenChat] Failed to rate message:', err);
      }
    },
    [token, store],
  );

  // ---------- loadStatus ----------
  const loadStatus = useCallback(
    async () => {
      if (!token) return;

      try {
        const raw = await krakenFetch<KrakenStatusBackendResponse>(
          '/api/kraken/status',
          token,
        );

        store.setRemaining(raw.remaining);
        store.setTier(raw.tier);
      } catch (err) {
        console.error('[KrakenChat] Failed to load status:', err);
      }
    },
    [token, store],
  );

  return {
    messages: store.messages,
    isTyping: store.isTyping,
    remainingMessages: store.remainingMessages,
    tier: store.tier,
    krakenState: store.krakenState,
    sessionId: store.sessionId,
    error: store.error,
    sendMessage,
    loadHistory,
    rateMessage,
    loadStatus,
  };
}
